import { Text, View } from "@/components";
import cn from "@/utils/cn";
import type { EventListProps } from "./types";

/**
 * EventsList Component
 *
 * @description
 * Displays a list of calendar events with details such as date, title, subtitle, time, and type.
 * Each event is rendered as a stylized card.
 *
 * Use this component to show a schedule or list of upcoming activities.
 *
 * @component
 * @param {EventListProps} props - The component props.
 * @param {CalendarEvent[]} props.events - Array of calendar events to render.
 *
 * @returns {JSX.Element} The rendered list of event cards.
 *
 * @example
 * ```tsx
 * const events = [{
 *   id: "1",
 *   startDate: "20",
 *   month: "MAR",
 *   title: "Reunión de equipo",
 *   type: "meeting",
 *   className: "bg-yellow-500",
 *   time: "10:00 AM"
 * }];
 *
 * <EventsList events={events} />
 * ```
 */
const EventsList = ({ events }: EventListProps) => (
  <View className="flex w-full flex-col gap-4">
    {events.map((event) => (
      <View
        key={event.id}
        className="flex flex-col md:flex-row items-stretch overflow-hidden rounded-2xl border border-border bg-card shadow-sm dark:bg-card"
      >
        <View className="flex min-w-[100px] flex-col items-center justify-center border-b border-border bg-card p-4 md:border-b-0 md:border-r">
          <Text className="text-lg font-bold text-foreground">
            {event.startDate}
            {event.endDate ? `-${event.endDate}` : ""}
          </Text>
          <Text className="text-xs font-bold uppercase text-muted-foreground">
            {event.month}
          </Text>
        </View>

        <View className="flex flex-1 flex-col justify-center gap-1 p-4 md:px-6">
          <Text type="h4" className="text-base font-bold text-foreground">
            {event.title}
          </Text>
          {event.subtitle && (
            <Text
              type="p"
              className="text-sm font-medium text-muted-foreground"
            >
              {event.subtitle}
            </Text>
          )}
          <Text type="p" className="text-sm text-muted-foreground">
            {event.time}
          </Text>
        </View>

        <View className="flex items-center p-4">
          <Text
            className={cn(
              "rounded-full px-6 py-2 text-xs font-bold uppercase tracking-wider",
              event.className
            )}
          >
            {event.title}
          </Text>
        </View>

        <View className={cn("w-2", event.className)} />
      </View>
    ))}
  </View>
);

export default EventsList;
